import React, { useEffect, useRef, useState } from "react";

import { Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/src/components/ui/button";
import { Input } from "@/src/components/ui/input";
import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";
import { cn } from "@/src/lib/utils";

import DataTableColumnVisibility from "./DataTableColumnVisibility";
import DataTableFacetedFilter from "./DataTableFacetedFilter";
import { CONTROL_SURFACE_CLASS } from "./dataTableStyles";

import type { RowData } from "@tanstack/react-table";
import type { JSX, ReactNode } from "react";

import type { DataTableInstance } from "./dataTable.types";

const SEARCH_DEBOUNCE_MS = 300;

interface DataTableToolbarProps<TData extends RowData> {
  table: DataTableInstance<TData>;
  search: string;
  filters: Record<string, string>;
  onSearchChange: (search: string) => void;
  onFilterChange: (columnId: string, value: string | null) => void;
  onReset: () => void;
  bulkActions?: ReactNode;
}

function DataTableToolbar<TData extends RowData>({
  table,
  search,
  filters,
  onSearchChange,
  onFilterChange,
  onReset,
  bulkActions,
}: DataTableToolbarProps<TData>): JSX.Element | null {
  const { t } = useTranslation("table");
  const [inputValue, setInputValue] = useState(search);
  const [previousSearch, setPreviousSearch] = useState(search);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // La recherche validée peut changer de l'extérieur (réinitialisation, URL) : on réaligne le champ pendant le rendu.
  if (search !== previousSearch) {
    setPreviousSearch(search);
    setInputValue(search);
  }

  useEffect(() => {
    if (inputValue === search) return undefined;

    const timer = setTimeout(
      () => onSearchChange(inputValue),
      SEARCH_DEBOUNCE_MS,
    );

    return () => clearTimeout(timer);
  }, [inputValue, search, onSearchChange]);

  // On vide aussi le champ : sinon la recherche en attente serait réappliquée après la réinitialisation.
  const handleReset = (): void => {
    setInputValue("");
    onReset();
  };

  // Effacer est immédiat ; le focus revient au champ, car le bouton disparaît.
  const handleClearSearch = (): void => {
    setInputValue("");
    if (search !== "") onSearchChange("");
    searchInputRef.current?.focus();
  };

  const leafColumnList = table.getAllLeafColumns();
  const isSearchable = leafColumnList.some(
    (column) => column.columnDef.meta?.searchable === true,
  );
  const filterColumnList = leafColumnList.filter(
    (column) => (column.columnDef.meta?.filterOptions?.length ?? 0) > 0,
  );
  const hideableColumnList = leafColumnList.filter((column) =>
    column.getCanHide(),
  );
  const isFiltered = search !== "" || Object.keys(filters).length > 0;

  if (
    !bulkActions &&
    !isSearchable &&
    filterColumnList.length === 0 &&
    hideableColumnList.length === 0
  ) {
    return null;
  }

  return (
    // Actions groupées et « Colonnes » à droite : leur apparition ne déplace ni la recherche ni les filtres.
    <div className="flex items-start gap-2">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        {isSearchable ? (
          <div className="relative">
            <Search className="text-muted-foreground pointer-events-none absolute top-2.5 left-2.5 size-4" />
            <Input
              ref={searchInputRef}
              aria-label={t("dataTable.search")}
              className={cn(CONTROL_SURFACE_CLASS, "h-9 w-64 pr-8 pl-8")}
              onChange={(event) => setInputValue(event.target.value)}
              placeholder={t("dataTable.search")}
              value={inputValue}
            />
            {inputValue !== "" ? (
              <Button
                aria-label={t("dataTable.clearSearch")}
                className="text-muted-foreground absolute top-1/2 right-1.5 -translate-y-1/2"
                data-tooltip-content={t("dataTable.clearSearch")}
                data-tooltip-id={APP_TOOLTIP_ID}
                onClick={handleClearSearch}
                size="icon-xs"
                variant="ghost"
              >
                <X />
              </Button>
            ) : null}
          </div>
        ) : null}
        {filterColumnList.map((column) => (
          <DataTableFacetedFilter
            key={column.id}
            column={column}
            onChange={(value) => onFilterChange(column.id, value)}
            showCounts={!table.options.manualFiltering}
            value={filters[column.id] ?? null}
          />
        ))}
        {isFiltered ? (
          <Button onClick={handleReset} size="lg" variant="ghost">
            {t("dataTable.reset")}
            <X />
          </Button>
        ) : null}
      </div>
      {bulkActions ? (
        <div className="flex shrink-0 items-center gap-2">{bulkActions}</div>
      ) : null}
      {hideableColumnList.length > 0 ? (
        <div className="shrink-0">
          <DataTableColumnVisibility columns={hideableColumnList} />
        </div>
      ) : null}
    </div>
  );
}

export default DataTableToolbar;
